import { validOpenWorldConversationSettings } from '@/lib/openWorlds/conversation'
import { validOpenWorldFork, type ForkOpenWorldPlaythroughRequest, type ForkOpenWorldPlaythroughResponse, type OpenWorldForkOrigin } from '@/lib/openWorlds/forks'
import { mapPersonaFromSupabase } from '@/api/mappers/personas'
import { supabase } from '@/lib/supabaseClient'
import { getStoredAppImage } from '@/lib/media/imageAssetFields'
import { openWorldCanonError } from '@/api/adapters/supabase/open-world-errors'
import { requireOpenWorldSession } from '@/api/adapters/supabase/open-world-auth'
import {
  OpenWorldPersistenceError,
  validateOpenWorldBaseline,
  validateOpenWorldDefinitionContent,
} from '@/lib/openWorlds/persistence'
import type {
  AttachOpenWorldPersonaRequest,
  OpenWorldPersonaSnapshot,
  CreateOpenWorldPlaythroughRequest,
  CreateOpenWorldPlaythroughResponse,
  GetOpenWorldDefinitionResponse,
  GetOpenWorldPlaythroughResponse,
  ListOpenWorldDefinitionsResponse,
  ListOpenWorldPlaythroughsResponse,
  OpenWorldDefinitionRecord,
  OpenWorldDefinitionSummary,
  OpenWorldListCursor,
  OpenWorldListRequest,
  OpenWorldPersistenceErrorCode,
  OpenWorldPlaythroughListRequest,
  OpenWorldPlaythroughRecord,
  OpenWorldPlaythroughSummary,
  OpenWorldValidationIssue,
  SaveOpenWorldDefinitionRequest,
  SaveOpenWorldDefinitionResponse,
} from '@/lib/openWorlds/persistence'

type UnknownRecord = Record<string, unknown>

export async function syncOpenWorldAvatarLeases(request: { sessionId: string; paths: string[] }): Promise<void> {
  const sessionId = requireUuid(request.sessionId, 'sessionId')
  if (!Array.isArray(request.paths) || request.paths.length > 3
    || request.paths.some(path => typeof path !== 'string' || !/^open-world-setups\/[0-9a-f-]{36}\/[0-9a-f-]{36}\.(webp|png|jpg|bmp)$/u.test(path))) {
    throw invalid('paths', 'Retain up to three setup avatar upload paths.')
  }
  await requireOpenWorldSession()
  const { data, error } = await supabase.rpc('rpc_sync_open_world_avatar_leases_v1', {
    p_session_id: sessionId, p_paths: [...new Set(request.paths)],
  })
  if (error) throw mapBackendError(error)
  if (data === true) {
    // This is best-effort acceleration. Scheduled cleanup also handles closed tabs.
    try { await supabase.functions.invoke('cleanup-open-world-avatars', { body: {} }) }
    catch { /* Successful lease/save results are independent of deletion. */ }
  }
}

const AVATAR_COLUMNS = [
  'avatar_url', 'avatar_provider', 'avatar_bucket', 'avatar_storage_path',
  'avatar_mime_type', 'avatar_bytes', 'avatar_width', 'avatar_height', 'avatar_original_name',
  'avatar_focus_x', 'avatar_focus_y',
]
const DEFINITION_SELECT = [
  ...AVATAR_COLUMNS,
  'initial_active_entry_ids',
  'opening_alternatives',
  'id',
  'title',
  'summary',
  'source_world_id',
  'configuration',
  'revision',
  'created_at',
  'updated_at',
].join(', ')
const DEFINITION_SUMMARY_SELECT = [
  'id',
  'title',
  'summary',
  'source_world_id',
  'revision',
  'created_at',
  'updated_at',
].join(', ')
const PLAYTHROUGH_SELECT = [
  'conversation_settings', 'display_title', 'opening_override',
  'id',
  'fork_origin',
  'definition_id',
  'definition_revision',
  'baseline',
  'persona',
  'revision',
  'active_head_id',
  'pending_attempt_id',
  'created_at',
  'updated_at',
].join(', ')
const PLAYTHROUGH_SUMMARY_SELECT = [
  'display_title',
  'id',
  'fork_origin',
  'definition_id',
  'definition_revision',
  'title:baseline->>title',
  'summary:baseline->>summary',
  'persona_name:persona->>persona_name',
  'revision',
  'active_head_id',
  'pending_attempt_id',
  'created_at',
  'updated_at',
].join(', ')

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const TIMESTAMP_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?(?:Z|[+-]\d{2}:\d{2})$/

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

function invalid(path: string, message: string): OpenWorldPersistenceError {
  return new OpenWorldPersistenceError(
    'invalid_input',
    `Invalid Open World input at ${path}: ${message}`,
    {
      issues: [{ path, message }],
    },
  )
}

function assertExactKeys(value: UnknownRecord, keys: readonly string[], path: string): void {
  const actual = Object.keys(value)
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(value, key))
      throw invalid(`${path}.${key}`, 'Required field is missing.')
  }
  const allowed = new Set(keys)
  const unknown = actual.find((key) => !allowed.has(key))
  if (unknown) throw invalid(`${path}.${unknown}`, 'Unknown field.')
}

function assertNoUnknownKeys(value: UnknownRecord, keys: readonly string[], path: string): void {
  const allowed = new Set(keys)
  const unknown = Object.keys(value).find((key) => !allowed.has(key))
  if (unknown) throw invalid(`${path}.${unknown}`, 'Unknown field.')
}

function requireUuid(value: unknown, path: string): string {
  if (typeof value !== 'string' || !UUID_RE.test(value)) throw invalid(path, 'Must be a UUID.')
  return value
}

function requirePositiveRevision(value: unknown, path: string): number {
  if (!Number.isSafeInteger(value) || (value as number) < 1 || (value as number) > 2_147_483_647) {
    throw invalid(path, 'Must be a positive integer no greater than 2147483647.')
  }
  return value as number
}

function requireTimestamp(value: unknown, path: string): string {
  if (
    typeof value !== 'string' ||
    !TIMESTAMP_RE.test(value) ||
    !Number.isFinite(Date.parse(value))
  ) {
    throw new OpenWorldPersistenceError('backend', `Malformed Open World response at ${path}.`)
  }
  return value
}

function requireResponseString(value: unknown, path: string): string {
  if (typeof value !== 'string')
    throw new OpenWorldPersistenceError('backend', `Malformed Open World response at ${path}.`)
  return value
}

function requireResponseUuid(value: unknown, path: string): string {
  if (typeof value !== 'string' || !UUID_RE.test(value)) {
    throw new OpenWorldPersistenceError('backend', `Malformed Open World response at ${path}.`)
  }
  return value
}

function requireResponseRevision(value: unknown, path: string): number {
  if (!Number.isInteger(value) || (value as number) < 1 || (value as number) > 2_147_483_647) {
    throw new OpenWorldPersistenceError('backend', `Malformed Open World response at ${path}.`)
  }
  return value as number
}

function requireResponseWorldId(value: unknown, path: string): number | null {
  if (value === null) return null
  if (!Number.isSafeInteger(value) || (value as number) < 1) {
    throw new OpenWorldPersistenceError('backend', `Malformed Open World response at ${path}.`)
  }
  return value as number
}

function responseRecord(value: unknown, path: string): UnknownRecord {
  if (!isRecord(value))
    throw new OpenWorldPersistenceError('backend', `Malformed Open World response at ${path}.`)
  return value
}

function mapDefinitionRow(value: unknown, path = 'definition'): OpenWorldDefinitionRecord {
  const row = responseRecord(value, path)
  assertBackendKeys(
    row,
    [
      ...AVATAR_COLUMNS.filter(key => Object.hasOwn(row, key)),
      ...(Object.hasOwn(row, 'avatar') ? ['avatar'] : []),
      ...(Object.hasOwn(row,'opening_alternatives')?['opening_alternatives']:[]),
      ...(Object.hasOwn(row, 'initial_active_entry_ids') ? ['initial_active_entry_ids'] : []),
      'id',
      'title',
      'summary',
      'source_world_id',
      'configuration',
      'revision',
      'created_at',
      'updated_at',
    ],
    path,
  )
  const content = validateOpenWorldDefinitionContent({
    ...(Object.hasOwn(row, 'avatar') ? { avatar: row.avatar }
      : Object.hasOwn(row, 'avatar_url') ? { avatar: getStoredAppImage(row, 'avatar') } : {}),
    title: row.title,
    summary: row.summary,
    sourceWorldId: row.source_world_id,
    configuration: row.configuration,
    ...(row.opening_alternatives===undefined?{}:{openingAlternatives:row.opening_alternatives}),
    ...(row.initial_active_entry_ids === undefined ? {} : { initialActiveEntryIds: row.initial_active_entry_ids }),
  })
  if (!content.success) {
    throw new OpenWorldPersistenceError('backend', `Malformed Open World response at ${path}.`, {
      issues: content.issues,
    })
  }
  return {
    id: requireResponseUuid(row.id, `${path}.id`),
    ...content.value,
    revision: requireResponseRevision(row.revision, `${path}.revision`),
    createdAt: requireTimestamp(row.created_at, `${path}.createdAt`),
    updatedAt: requireTimestamp(row.updated_at, `${path}.updatedAt`),
  }
}

function mapRpcDefinition(value: unknown): OpenWorldDefinitionRecord {
  const row = responseRecord(value, 'definition')
  assertBackendKeys(
    row,
    [
      ...(Object.hasOwn(row, 'avatar') ? ['avatar'] : []),
      'id',
      'title',
      'summary',
      ...(Object.hasOwn(row,'openingAlternatives')?['openingAlternatives']:[]),
      ...(Object.hasOwn(row, 'initialActiveEntryIds') ? ['initialActiveEntryIds'] : []),
      'sourceWorldId',
      'configuration',
      'revision',
      'createdAt',
      'updatedAt',
    ],
    'definition',
  )
  return mapDefinitionRow({
    ...(Object.hasOwn(row, 'avatar') ? { avatar: row.avatar } : {}),
    ...(row.openingAlternatives===undefined?{}:{opening_alternatives:row.openingAlternatives}),
    ...(row.initialActiveEntryIds === undefined ? {} : { initial_active_entry_ids: row.initialActiveEntryIds }),
    id: row.id,
    title: row.title,
    summary: row.summary,
    source_world_id: row.sourceWorldId,
    configuration: row.configuration,
    revision: row.revision,
    created_at: row.createdAt,
    updated_at: row.updatedAt,
  })
}

function assertBackendKeys(value: UnknownRecord, keys: readonly string[], path: string): void {
  const actual = Object.keys(value)
  if (
    actual.length !== keys.length ||
    keys.some((key) => !Object.prototype.hasOwnProperty.call(value, key))
  ) {
    throw new OpenWorldPersistenceError('backend', `Malformed Open World response at ${path}.`)
  }
}

function mapDefinitionSummary(value: unknown, path: string): OpenWorldDefinitionSummary {
  const row = responseRecord(value, path)
  assertBackendKeys(row, ['id', 'title', 'summary', 'source_world_id', 'revision', 'created_at', 'updated_at'], path)
  return {
    id: requireResponseUuid(row.id, `${path}.id`),
    title: requireResponseString(row.title, `${path}.title`),
    summary: requireResponseString(row.summary, `${path}.summary`),
    sourceWorldId: requireResponseWorldId(row.source_world_id, `${path}.sourceWorldId`),
    revision: requireResponseRevision(row.revision, `${path}.revision`),
    createdAt: requireTimestamp(row.created_at, `${path}.createdAt`),
    updatedAt: requireTimestamp(row.updated_at, `${path}.updatedAt`),
  }
}

function mapPersonaSnapshot(value: unknown): OpenWorldPersonaSnapshot | null {
  if (value === null) return null
  const row = responseRecord(value, 'persona')
  if (!Number.isSafeInteger(row.id) || (row.id as number) < 1 || typeof row.persona_name !== 'string'
    || !row.persona_name.trim() || typeof row.description !== 'string'
    || typeof row.pronouns !== 'string' || !['she/her', 'he/him', 'they/them'].includes(row.pronouns)
    || typeof row.imgpath !== 'string') {
    throw new OpenWorldPersistenceError('backend', 'Malformed Open World persona snapshot.')
  }
  const persona = mapPersonaFromSupabase({ ...row, id: row.id as number })
  return { id: persona.id, persona_name: persona.persona_name, description: persona.description,
    pronouns: persona.pronouns, imgpath: persona.imgpath, img_focus: persona.img_focus,
    attachedAt: requireTimestamp(row.attachedAt, 'persona.attachedAt'),
    attachedHeadId: row.attachedHeadId === null ? null : requireResponseUuid(row.attachedHeadId, 'persona.attachedHeadId') }
}

function mapForkOrigin(value: unknown): OpenWorldForkOrigin | null {
  if (value === undefined || value === null) return null
  const row = responseRecord(value, 'forkOrigin')
  assertBackendKeys(row, ['playthroughId', 'messageId', 'messageRole', 'turnNumber'], 'forkOrigin')
  if (!['opening', 'player', 'storyteller'].includes(row.messageRole as string) ||
    !Number.isSafeInteger(row.turnNumber) || (row.turnNumber as number) < 0 ||
    (row.messageRole === 'opening' ? row.messageId !== null || row.turnNumber !== 0 : !UUID_RE.test(String(row.messageId)) || (row.turnNumber as number) < 1)) {
    throw new OpenWorldPersistenceError('backend', 'Malformed Open World fork origin.')
  }
  return { playthroughId: requireResponseUuid(row.playthroughId, 'forkOrigin.playthroughId'),
    messageId: row.messageId as string | null, messageRole: row.messageRole as OpenWorldForkOrigin['messageRole'],
    turnNumber: row.turnNumber as number }
}

export async function forkOpenWorldPlaythrough(request: ForkOpenWorldPlaythroughRequest): Promise<ForkOpenWorldPlaythroughResponse> {
  if (!validOpenWorldFork(request)) throw invalid('request', 'Invalid fork request.')
  await requireOpenWorldSession()
  const { data, error } = await supabase.rpc(request.duplicateSetup ? 'rpc_fork_open_world_playthrough_v2' : 'rpc_fork_open_world_playthrough_v1', {
    p_id: request.id, p_receipt_id: request.receiptId, p_playthrough_id: request.playthroughId,
    p_expected_revision: request.expectedRevision, p_expected_head_id: request.expectedHeadId,
    p_message_id: request.messageId, p_message_role: request.messageRole,
    ...(request.duplicateSetup ? {
      p_setup_id: request.duplicateSetup.id,
      p_expected_setup_revision: request.duplicateSetup.expectedRevision,
    } : {}),
  })
  if (error) throw mapBackendError(error)
  const response = responseRecord(data, 'response')
  assertBackendKeys(response, ['playthrough', 'replayed'], 'response')
  if (typeof response.replayed !== 'boolean') throw new OpenWorldPersistenceError('backend', 'Malformed Open World fork response.')
  const playthrough = mapRpcPlaythrough(response.playthrough)
  if (playthrough.id !== request.id || playthrough.forkOrigin?.playthroughId !== request.playthroughId ||
    playthrough.forkOrigin.messageId !== request.messageId || playthrough.forkOrigin.messageRole !== request.messageRole ||
    (request.duplicateSetup && playthrough.definitionId !== request.duplicateSetup.id)) {
    throw new OpenWorldPersistenceError('backend', 'Malformed Open World fork acknowledgement.')
  }
  return { playthrough, replayed: response.replayed }
}

function mapPlaythroughRow(value: unknown, path = 'playthrough'): OpenWorldPlaythroughRecord {
  const row = responseRecord(value, path)
  assertBackendKeys(
    row,
    [
      'id',
      ...(Object.hasOwn(row, 'fork_origin') ? ['fork_origin'] : []),
      ...['conversation_settings','display_title','opening_override'].filter((key) => Object.hasOwn(row, key)),
      'definition_id',
      'definition_revision',
      'baseline',
      'persona',
      'revision',
      'active_head_id',
      'pending_attempt_id',
      'created_at',
      'updated_at',
    ],
    path,
  )
  if (row.conversation_settings !== undefined && !validOpenWorldConversationSettings(row.conversation_settings)) throw new OpenWorldPersistenceError('backend','Malformed conversation settings.')
  for (const key of ['display_title','opening_override']) {
    if (row[key] !== undefined && row[key] !== null && (typeof row[key] !== 'string' || !row[key].trim())) throw new OpenWorldPersistenceError('backend','Malformed conversation text.')
  }
  const baseline = validateOpenWorldBaseline(row.baseline)
  if (!baseline.success) {
    throw new OpenWorldPersistenceError('backend', `Malformed Open World response at ${path}.`, {
      issues: baseline.issues,
    })
  }
  return {
    ...(row.conversation_settings === undefined ? {} : {conversationSettings: structuredClone(row.conversation_settings)}),
    ...(row.display_title === undefined ? {} : {displayTitle: row.display_title as string | null}),
    ...(row.opening_override === undefined ? {} : {openingOverride: row.opening_override as string | null}),
    forkOrigin: mapForkOrigin(row.fork_origin),
    id: requireResponseUuid(row.id, `${path}.id`),
    definitionId: requireResponseUuid(row.definition_id, `${path}.definitionId`),
    definitionRevision: requireResponseRevision(
      row.definition_revision,
      `${path}.definitionRevision`,
    ),
    baseline: baseline.value,
    persona: mapPersonaSnapshot(row.persona),
    revision: requireResponseRevision(row.revision, `${path}.revision`),
    activeHeadId:
      row.active_head_id === null
        ? null
        : requireResponseUuid(row.active_head_id, `${path}.activeHeadId`),
    pendingAttemptId:
      row.pending_attempt_id === null
        ? null
        : requireResponseUuid(row.pending_attempt_id, `${path}.pendingAttemptId`),
    createdAt: requireTimestamp(row.created_at, `${path}.createdAt`),
    updatedAt: requireTimestamp(row.updated_at, `${path}.updatedAt`),
  }
}

function mapRpcPlaythrough(value: unknown): OpenWorldPlaythroughRecord {
  const row = responseRecord(value, 'playthrough')
  assertBackendKeys(
    row,
    [
      ...(Object.hasOwn(row,'openingOverride')?['openingOverride']:[]),
      'id',
      ...(Object.hasOwn(row, 'forkOrigin') ? ['forkOrigin'] : []),
      'definitionId',
      'definitionRevision',
      'baseline',
      'persona',
      'revision',
      'activeHeadId',
      'pendingAttemptId',
      'createdAt',
      'updatedAt',
    ],
    'playthrough',
  )
  return mapPlaythroughRow({
    fork_origin: row.forkOrigin ?? null,
    ...(row.openingOverride===undefined?{}:{opening_override:row.openingOverride}),
    id: row.id,
    definition_id: row.definitionId,
    definition_revision: row.definitionRevision,
    baseline: row.baseline,
    persona: row.persona,
    revision: row.revision,
    active_head_id: row.activeHeadId,
    pending_attempt_id: row.pendingAttemptId,
    created_at: row.createdAt,
    updated_at: row.updatedAt,
  })
}

function mapPlaythroughSummary(value: unknown, path: string): OpenWorldPlaythroughSummary {
  const row = responseRecord(value, path)
  assertBackendKeys(
    row,
    [
      'id',
      ...(Object.hasOwn(row, 'fork_origin') ? ['fork_origin'] : []),
      ...['preview','display_title'].filter(key=>Object.hasOwn(row,key)),
      'definition_id',
      'definition_revision',
      'title',
      'summary',
      'persona_name',
      'revision',
      'active_head_id',
      'pending_attempt_id',
      'created_at',
      'updated_at',
    ],
    path,
  )
  return {
    forkOrigin: mapForkOrigin(row.fork_origin),
    id: requireResponseUuid(row.id, `${path}.id`),
    definitionId: requireResponseUuid(row.definition_id, `${path}.definitionId`),
    definitionRevision: requireResponseRevision(
      row.definition_revision,
      `${path}.definitionRevision`,
    ),
    setupTitle: requireResponseString(row.title, `${path}.title`),
    title: row.display_title === undefined || row.display_title === null ? requireResponseString(row.title, `${path}.title`) : requireResponseString(row.display_title,`${path}.displayTitle`),
    ...(row.preview === undefined ? {} : {preview:requireResponseString(row.preview,`${path}.preview`)}),
    summary: requireResponseString(row.summary, `${path}.summary`),
    personaName: row.persona_name === null ? null : requireResponseString(row.persona_name, `${path}.personaName`),
    revision: requireResponseRevision(row.revision, `${path}.revision`),
    activeHeadId:
      row.active_head_id === null
        ? null
        : requireResponseUuid(row.active_head_id, `${path}.activeHeadId`),
    pendingAttemptId:
      row.pending_attempt_id === null
        ? null
        : requireResponseUuid(row.pending_attempt_id, `${path}.pendingAttemptId`),
    createdAt: requireTimestamp(row.created_at, `${path}.createdAt`),
    updatedAt: requireTimestamp(row.updated_at, `${path}.updatedAt`),
  }
}

function parseIssues(details: unknown): OpenWorldValidationIssue[] {
  if (typeof details !== 'string') return []
  try {
    const parsed: unknown = JSON.parse(details)
    if (!isRecord(parsed) || !Array.isArray(parsed.issues)) return []
    return parsed.issues.flatMap((item): OpenWorldValidationIssue[] => {
      if (!isRecord(item) || typeof item.path !== 'string' || typeof item.message !== 'string')
        return []
      return [{ path: item.path, message: item.message }]
    })
  } catch {
    return []
  }
}

function mapBackendError(error: unknown): OpenWorldPersistenceError {
  if (error instanceof OpenWorldPersistenceError) return error
  const record = isRecord(error) ? error : {}
  if (record.message === 'OW_RELOAD_REQUIRED') return openWorldCanonError({ message: record.message })!
  const message =
    typeof record.message === 'string' ? record.message : 'Open World persistence request failed.'
  const domainMatch = /^OW_(UNAUTHENTICATED|NOT_FOUND|CONFLICT|INVALID_INPUT|SOURCE_REQUIRED|SOURCE_UNAVAILABLE|SOURCE_UNPUBLISHED|SOURCE_INVALID)$/.exec(message)
  if (record.code === 'P0001' && domainMatch) {
    const code = domainMatch[1]!.toLowerCase() as OpenWorldPersistenceErrorCode
    const issues = code === 'invalid_input' || code === 'source_invalid'
      ? parseIssues(record.details)
      : []
    const friendlyMessage: Partial<Record<OpenWorldPersistenceErrorCode, string>> = {
      source_required: 'Choose a World before starting a playthrough.',
      source_unavailable: 'The selected World is unavailable or you no longer have access to it.',
      source_unpublished: 'Publish the selected World in Creator before starting a playthrough.',
      source_invalid: 'The selected World’s published canon is not compatible with Open Worlds.',
    }
    const issueSuffix = code === 'source_invalid' && issues[0]
      ? ` ${issues[0].path}: ${issues[0].message}`
      : ''
    return new OpenWorldPersistenceError(code, `${friendlyMessage[code] ?? message}${issueSuffix}`, {
      issues,
      cause: error,
    })
  }
  return new OpenWorldPersistenceError('backend', message, { cause: error })
}

function validateCursor(cursor: unknown): OpenWorldListCursor | null {
  if (cursor === undefined || cursor === null) return null
  if (!isRecord(cursor)) throw invalid('cursor', 'Must be an object.')
  assertExactKeys(cursor, ['updatedAt', 'id'], 'cursor')
  const id = requireUuid(cursor.id, 'cursor.id')
  if (
    typeof cursor.updatedAt !== 'string' ||
    !TIMESTAMP_RE.test(cursor.updatedAt) ||
    !Number.isFinite(Date.parse(cursor.updatedAt))
  ) {
    throw invalid('cursor.updatedAt', 'Must be an RFC 3339 timestamp with a timezone.')
  }
  return { updatedAt: cursor.updatedAt, id }
}

function validateListRequest(
  request: unknown,
  playthroughs: boolean,
): {
  limit: number
  cursor: OpenWorldListCursor | null
  definitionId?: string
} {
  if (request === undefined) return { limit: 20, cursor: null }
  if (!isRecord(request)) throw invalid('request', 'Must be an object.')
  assertNoUnknownKeys(
    request,
    playthroughs ? ['limit', 'cursor', 'definitionId'] : ['limit', 'cursor'],
    'request',
  )
  const limit = request.limit === undefined ? 20 : request.limit
  if (!Number.isInteger(limit) || (limit as number) < 1 || (limit as number) > 50) {
    throw invalid('limit', 'Must be an integer from 1 through 50.')
  }
  const result: { limit: number; cursor: OpenWorldListCursor | null; definitionId?: string } = {
    limit: limit as number,
    cursor: validateCursor(request.cursor),
  }
  if (playthroughs && request.definitionId !== undefined) {
    result.definitionId = requireUuid(request.definitionId, 'definitionId')
  }
  return result
}

function applyCursor<T extends { or: (filter: string) => T }>(
  query: T,
  cursor: OpenWorldListCursor | null,
): T {
  if (!cursor) return query
  return query.or(
    `updated_at.lt.${cursor.updatedAt},and(updated_at.eq.${cursor.updatedAt},id.lt.${cursor.id})`,
  )
}

export async function saveOpenWorldDefinition(
  request: SaveOpenWorldDefinitionRequest,
): Promise<SaveOpenWorldDefinitionResponse> {
  if (!isRecord(request)) throw invalid('request', 'Must be an object.')
  assertExactKeys(
    request,
    ['id', 'mutationId', 'expectedRevision', 'title', 'summary', 'sourceWorldId', 'configuration', ...(Object.hasOwn(request, 'avatar') ? ['avatar'] : []),...(Object.hasOwn(request,'openingAlternatives')?['openingAlternatives']:[]), ...(Object.hasOwn(request, 'initialActiveEntryIds') ? ['initialActiveEntryIds'] : [])],
    'request',
  )
  const id = requireUuid(request.id, 'id')
  const mutationId = requireUuid(request.mutationId, 'mutationId')
  const expectedRevision =
    request.expectedRevision === null
      ? null
      : requirePositiveRevision(request.expectedRevision, 'expectedRevision')
  const content = validateOpenWorldDefinitionContent({
    ...(Object.hasOwn(request, 'avatar') ? { avatar: request.avatar } : {}),
    title: request.title,
    summary: request.summary,
    sourceWorldId: request.sourceWorldId,
    configuration: request.configuration,
    ...(request.openingAlternatives===undefined?{}:{openingAlternatives:request.openingAlternatives}),
    ...(request.initialActiveEntryIds === undefined ? {} : { initialActiveEntryIds: request.initialActiveEntryIds }),
  })
  if (!content.success) {
    throw new OpenWorldPersistenceError('invalid_input', 'Open World definition is invalid.', {
      issues: content.issues,
    })
  }
  await requireOpenWorldSession()
  const payload = structuredClone(content.value)
  const { data, error } = await supabase.rpc('rpc_save_open_world_definition_v4', {
    p_id: id,
    p_mutation_id: mutationId,
    p_expected_revision: expectedRevision,
    p_title: payload.title,
    p_summary: payload.summary,
    p_source_world_id: payload.sourceWorldId,
    p_configuration: payload.configuration,
    p_opening_alternatives: payload.openingAlternatives ?? [],
    p_initial_active_entry_ids: payload.initialActiveEntryIds ?? [],
    p_avatar: payload.avatar ?? null,
  })
  if (error) throw mapBackendError(error)
  const response = responseRecord(data, 'response')
  assertBackendKeys(response, ['definition', 'replayed'], 'response')
  if (typeof response.replayed !== 'boolean')
    throw new OpenWorldPersistenceError(
      'backend',
      'Malformed Open World response at response.replayed.',
    )
  return { definition: mapRpcDefinition(response.definition), replayed: response.replayed }
}

export async function getOpenWorldDefinition(id: string): Promise<GetOpenWorldDefinitionResponse> {
  const checkedId = requireUuid(id, 'id')
  await requireOpenWorldSession()
  const { data, error } = await supabase
    .from('open_world_definitions')
    .select(DEFINITION_SELECT)
    .eq('id', checkedId)
    .maybeSingle()
  if (error) throw mapBackendError(error)
  return { definition: data === null ? null : mapDefinitionRow(data) }
}

export async function listOpenWorldDefinitions(
  request?: OpenWorldListRequest,
): Promise<ListOpenWorldDefinitionsResponse> {
  const { limit, cursor } = validateListRequest(request, false)
  await requireOpenWorldSession()
  let query = supabase
    .from('open_world_definitions')
    .select(DEFINITION_SUMMARY_SELECT)
    .order('updated_at', { ascending: false })
    .order('id', { ascending: false })
    .limit(limit + 1)
  query = applyCursor(query, cursor)
  const { data, error } = await query
  if (error) throw mapBackendError(error)
  if (!Array.isArray(data))
    throw new OpenWorldPersistenceError('backend', 'Malformed Open World definition list response.')
  const mapped = data.map((row, index) => mapDefinitionSummary(row, `items[${index}]`))
  const items = mapped.slice(0, limit)
  const last = items.at(-1)
  return {
    items,
    nextCursor: mapped.length > limit && last ? { updatedAt: last.updatedAt, id: last.id } : null,
  }
}

export async function createOpenWorldPlaythrough(
  request: CreateOpenWorldPlaythroughRequest,
): Promise<CreateOpenWorldPlaythroughResponse> {
  if (!isRecord(request)) throw invalid('request', 'Must be an object.')
  assertExactKeys(request, ['id', 'definitionId', 'expectedDefinitionRevision', 'personaId',...(Object.hasOwn(request,'openingIndex')?['openingIndex']:[])], 'request')
  const openingIndex=request.openingIndex ?? 0
  if (!Number.isInteger(openingIndex) || openingIndex<0 || openingIndex>8) throw invalid('openingIndex','Choose an authored opening.')
  const personaId = requirePositiveRevision(request.personaId, 'personaId')
  const id = requireUuid(request.id, 'id')
  const definitionId = requireUuid(request.definitionId, 'definitionId')
  const expectedDefinitionRevision = requirePositiveRevision(
    request.expectedDefinitionRevision,
    'expectedDefinitionRevision',
  )
  await requireOpenWorldSession()
  const { data, error } = await supabase.rpc('rpc_create_open_world_playthrough_v3', {
    p_id: id,
    p_definition_id: definitionId,
    p_expected_definition_revision: expectedDefinitionRevision,
    p_persona_id: personaId,
    p_opening_index:openingIndex,
  })
  if (error) throw mapBackendError(error)
  const response = responseRecord(data, 'response')
  assertBackendKeys(response, ['playthrough', 'replayed'], 'response')
  if (typeof response.replayed !== 'boolean')
    throw new OpenWorldPersistenceError(
      'backend',
      'Malformed Open World response at response.replayed.',
    )
  return { playthrough: mapRpcPlaythrough(response.playthrough), replayed: response.replayed }
}

export async function attachOpenWorldPersona(request: AttachOpenWorldPersonaRequest): Promise<CreateOpenWorldPlaythroughResponse> {
  if (!isRecord(request)) throw invalid('request', 'Must be an object.')
  assertExactKeys(request, ['playthroughId', 'personaId', 'expectedRevision', 'expectedHeadId'], 'request')
  const playthroughId = requireUuid(request.playthroughId, 'playthroughId')
  const personaId = requirePositiveRevision(request.personaId, 'personaId')
  const expectedRevision = requirePositiveRevision(request.expectedRevision, 'expectedRevision')
  const expectedHeadId = request.expectedHeadId === null ? null : requireUuid(request.expectedHeadId, 'expectedHeadId')
  await requireOpenWorldSession()
  const { data, error } = await supabase.rpc('rpc_attach_open_world_persona_v1', {
    p_playthrough_id: playthroughId, p_persona_id: personaId,
    p_expected_revision: expectedRevision, p_expected_head_id: expectedHeadId,
  })
  if (error) throw mapBackendError(error)
  const response = responseRecord(data, 'response')
  assertBackendKeys(response, ['playthrough', 'replayed'], 'response')
  if (typeof response.replayed !== 'boolean') throw new OpenWorldPersistenceError('backend', 'Malformed persona attachment response.')
  return { playthrough: mapRpcPlaythrough(response.playthrough), replayed: response.replayed }
}

export async function getOpenWorldPlaythrough(
  id: string,
): Promise<GetOpenWorldPlaythroughResponse> {
  const checkedId = requireUuid(id, 'id')
  await requireOpenWorldSession()
  const { data, error } = await supabase
    .from('open_world_playthroughs')
    .select(PLAYTHROUGH_SELECT)
    .eq('id', checkedId)
    .maybeSingle()
  if (error) throw mapBackendError(error)
  return { playthrough: data === null ? null : mapPlaythroughRow(data) }
}

export async function listOpenWorldPlaythroughs(
  request?: OpenWorldPlaythroughListRequest,
): Promise<ListOpenWorldPlaythroughsResponse> {
  if (request?.sort !== undefined || request?.search !== undefined) return listManagedThreads(request)
  const { limit, cursor, definitionId } = validateListRequest(request, true)
  await requireOpenWorldSession()
  let query = supabase
    .from('open_world_playthroughs')
    .select(PLAYTHROUGH_SUMMARY_SELECT)
    .order('updated_at', { ascending: false })
    .order('id', { ascending: false })
    .limit(limit + 1)
  if (definitionId) query = query.eq('definition_id', definitionId)
  query = applyCursor(query, cursor)
  const { data, error } = await query
  if (error) throw mapBackendError(error)
  if (!Array.isArray(data))
    throw new OpenWorldPersistenceError(
      'backend',
      'Malformed Open World playthrough list response.',
    )
  const mapped = data.map((row, index) => mapPlaythroughSummary(row, `items[${index}]`))
  const items = mapped.slice(0, limit)
  const last = items.at(-1)
  return {
    items,
    nextCursor: mapped.length > limit && last ? { updatedAt: last.updatedAt, id: last.id } : null,
  }
}

async function rpc(name:string,parameters:UnknownRecord):Promise<unknown> {
  await requireOpenWorldSession()
  const {data,error}=await supabase.rpc(name,parameters)
  if(error)throw mapBackendError(error)
  return data
}

async function listManagedThreads(request: OpenWorldPlaythroughListRequest): Promise<ListOpenWorldPlaythroughsResponse> {
  const {sort='activity',search='',...base}=request
  if (!['activity','created','title'].includes(sort) || typeof search !== 'string' || new TextEncoder().encode(search).length>640) throw invalid('search','Thread search or sort is invalid.')
  if (base.cursor) {
    assertExactKeys(base.cursor as unknown as UnknownRecord,['id','updatedAt','sortValue'],'cursor')
    if (typeof base.cursor.sortValue !== 'string' || base.cursor.sortValue.length>2048 ||
      (sort==='created' && !Number.isFinite(Date.parse(base.cursor.sortValue)))) throw invalid('cursor','Thread cursor is invalid.')
  }
  const checked=validateListRequest({...base,cursor:base.cursor?{id:base.cursor.id,updatedAt:base.cursor.updatedAt}:null},true)
  const value=responseRecord(await rpc('rpc_list_open_world_threads_v1',{
    p_definition_id:checked.definitionId ?? null,p_limit:checked.limit,p_cursor:base.cursor ?? null,p_search:search,p_sort:sort,
  }),'threads')
  assertExactKeys(value,['items','nextCursor'],'threads')
  if (!Array.isArray(value.items)) throw new OpenWorldPersistenceError('backend','Malformed thread list.')
  let cursor:OpenWorldListCursor|null=null
  if (value.nextCursor !== null) {
    const raw=responseRecord(value.nextCursor,'nextCursor')
    assertBackendKeys(raw,['id','updatedAt','sortValue'],'nextCursor')
    cursor={id:requireResponseUuid(raw.id,'nextCursor.id'),updatedAt:requireTimestamp(raw.updatedAt,'nextCursor.updatedAt'),sortValue:requireResponseString(raw.sortValue,'nextCursor.sortValue')}
  }
  return {items:value.items.map((row,i)=>mapPlaythroughSummary(row,`items[${i}]`)),nextCursor:cursor}
}

export async function deleteOpenWorldPlaythrough(request:{id:string;receiptId:string;expectedRevision:number}):Promise<{id:string;replayed:boolean}> {
  const id=requireUuid(request.id,'id'),receipt=requireUuid(request.receiptId,'receiptId')
  if (!Number.isInteger(request.expectedRevision) || request.expectedRevision<1) throw invalid('expectedRevision','Thread revision is invalid.')
  const value=responseRecord(await rpc('rpc_delete_open_world_playthrough_v1',{p_id:id,p_receipt_id:receipt,p_expected_revision:request.expectedRevision}),'deletion')
  assertBackendKeys(value,['id','replayed'],'deletion')
  if (value.id!==id || typeof value.replayed!=='boolean') throw new OpenWorldPersistenceError('backend','Malformed deletion acknowledgement.')
  return {id,replayed:value.replayed}
}
